import { Link } from "react-router-dom";
import { ArrowRight, Target, Lightbulb, Users, Compass, CheckCircle2 } from "lucide-react";

const ChiSiamoPage = () => {
  const values = [
    {
      icon: Target,
      title: "Visione",
      number: "01",
      description: "Crediamo che la tecnologia debba essere uno strumento di semplificazione, non un ulteriore ostacolo. Per questo iSi è progettato per eliminare passaggi superflui, automatizzare processi e ridurre i costi legati alla gestione tradizionale del registratore telematico. Integrità, affidabilità e innovazione guidano ogni sviluppo."
    },
    {
      icon: Lightbulb,
      title: "Soluzione",
      number: "02",
      description: "iSi integra gestione delle vendite, controllo del magazzino e invio automatico dei documenti commerciali direttamente al cassetto fiscale tramite le credenziali dell'Agenzia delle Entrate. Un unico ecosistema digitale che garantisce visione in tempo reale, riduzione dei costi operativi e maggiore efficienza."
    },
    {
      icon: Users,
      title: "A Chi è Dedicato",
      number: "03",
      description: "Ristoranti, bar, negozi, attività commerciali, lavanderie, società sportive e qualsiasi realtà che necessita di una gestione efficiente delle vendite e della fiscalità. Dal piccolo esercente alla catena retail, iSiAP si adatta alle esigenze di ogni dimensione."
    },
    {
      icon: Compass,
      title: "Approccio",
      number: "04",
      description: "Non si tratta solo di digitalizzare, ma di ottimizzare. Non solo di adeguarsi, ma di evolvere. Il nostro team lavora a stretto contatto con i clienti per comprendere le specifiche esigenze e offrire soluzioni personalizzate che generano valore reale."
    }
  ];

  return (
    <div data-testid="chi-siamo-page">
      {/* Hero Section */}
      <section className="bg-[#f5f6fa] section-padding" data-testid="about-hero">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <span className="badge mb-6">Chi Siamo</span>
            <h1 className="heading-1 text-[#1a1f36] mb-8">
              Semplifichiamo la gestione delle attività commerciali
            </h1>
            <p className="body-large max-w-3xl mx-auto">
              iSi nasce per semplificare la gestione delle attività commerciali in un contesto 
              digitale e regolamentato. Trasformiamo complessità operative e obblighi fiscali 
              in una piattaforma integrata e intuitiva.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding" data-testid="values-section">
        <div className="section-container">
          <div className="space-y-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className={`grid md:grid-cols-12 gap-8 items-start p-8 md:p-12 rounded-2xl ${
                  index % 2 === 0 ? 'bg-white border border-[#e2e4ed]' : 'bg-[#f5f6fa]'
                }`}
                data-testid={`value-${value.title.toLowerCase().replace(" ", "-")}`}
              >
                <div className="md:col-span-4 flex items-start gap-6">
                  <div className="number-badge shrink-0">{value.number}</div>
                  <div>
                    <h3 className="heading-3 text-[#1a1f36] mb-2">{value.title}</h3>
                    <div className="icon-container mt-4">
                      <value.icon size={24} />
                    </div>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <p className="text-[#4a5068] text-lg leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info Section - Navy */}
      <section className="bg-[#1a1f36] section-padding" data-testid="company-info">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider mb-4 block">
                La Nostra Storia
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Un prodotto di 4D Sistemi Informatici
              </h2>
              <p className="text-[#9ca3af] text-lg leading-relaxed mb-8">
                iSiAP è sviluppato da 4D Sistemi Informatici, azienda con oltre 15 anni di 
                esperienza nel settore delle soluzioni informatiche per il retail e la ristorazione. 
                La nostra missione è rendere accessibile a tutti la tecnologia che semplifica il lavoro quotidiano.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  "Oltre 900 punti vendita attivi",
                  "Supporto tecnico dedicato 24/7",
                  "Aggiornamenti continui inclusi",
                  "Conformità normativa garantita"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white">
                    <CheckCircle2 size={20} className="text-[#FF6B00] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link 
                to="/contatti" 
                className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#E55F00] transition-colors"
                data-testid="about-cta"
              >
                Contattaci
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="/images/team.jpg"
                  alt="Team at work"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="about-cta-section">
        <div className="section-container">
          <div className="bg-[#f5f6fa] rounded-2xl p-12 md:p-16 text-center border border-[#e2e4ed]">
            <h2 className="heading-2 text-[#1a1f36] mb-6">
              Vuoi saperne di più?
            </h2>
            <p className="body-large max-w-2xl mx-auto mb-8">
              Il nostro team è a disposizione per rispondere a tutte le tue domande 
              e mostrarti come iSiAP può aiutare la tua attività.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contatti" className="btn-primary" data-testid="about-final-cta">
                Richiedi Informazioni
                <ArrowRight size={18} />
              </Link>
              <Link to="/soluzioni" className="btn-secondary">
                Esplora le Soluzioni
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamoPage;
